// ============================================================
// ConferenceCallRoom — the in-app custom Daily "black studio" call, enhanced
// into a Meet-like interactive room (PlayStation + Meet visual system).
//
// OUR tiles, OUR controls, no Daily chrome. Media tracks attach to our own
// <video>/<audio>. Every state-machine phase renders an honest surface. The
// stage supports Spotlight / Grid / Filmstrip / Screen-share layouts driven by
// the real participant snapshot; the control dock exposes only real handlers;
// the People tab is live off the call object and every other tab names its
// canonical Square Code owner and stays blocked/deferred until wired (no
// placeholder is presented as live). Recording/transcription are deferred.
//
// Renders inside <CallProvider>; reads state via useCall().
// ============================================================

import { useEffect, useState } from 'react'
import { useRef } from 'react'
import { useCall } from './callContext'
import type { CallControls } from './callContext'
import type { CallParticipant, MediaDevice } from './call.types'
import {
  availableLayouts,
  resolveLayout,
  stageParticipant,
  tileState,
  initialsOf,
  remoteAudioParticipants,
  phaseAnnouncement,
  CONFERENCE_PANELS,
  type PanelDef,
  type RoomLayout,
} from './conferenceRoomModel'
import { WaitingRoomHostPanel } from './waitingRoomPanels'
import { ChatPanel } from './ChatPanel'
import { MeetingNotesPanel, MeetingFilesPanel } from './MeetingWorkspacePanels'
import { TasksPanel } from './TasksPanel'
import { ApprovalsPanel } from './ApprovalsPanel'
import { api } from '../../../../api/client'
import './conference-room.css'
import './conference-room-enhanced.css'

const MONO = 'var(--sc-font-mono, "JetBrains Mono", ui-monospace, monospace)'
const ACCENT = '#0891b2'
const OK = '#059669'
const WARN = '#b8862a'
const DANGER = '#dc2626'
const OLIVIUM = '#7c5cff'
const MUTED = '#8a93a3'

// ---- media elements: attach a MediaStreamTrack to our own DOM ---------------
function VideoEl({ track, muted, mirror }: { track?: MediaStreamTrack; muted?: boolean; mirror?: boolean }) {
  const ref = useRef<HTMLVideoElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (track) { el.srcObject = new MediaStream([track]); el.play?.().catch(() => {}) }
    else el.srcObject = null
  }, [track])
  return <video ref={ref} autoPlay playsInline muted={muted} style={{ width: '100%', height: '100%', objectFit: 'cover', transform: mirror ? 'scaleX(-1)' : 'none', display: 'block' }} />
}
function AudioEl({ track }: { track?: MediaStreamTrack }) {
  const ref = useRef<HTMLAudioElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (track) { el.srcObject = new MediaStream([track]); el.play?.().catch(() => {}) }
    else el.srcObject = null
  }, [track])
  return <audio ref={ref} autoPlay />
}

// Remote audio sink — mounted once whenever we are in a call, independent of the
// visual layout, so spotlight/screen-share never silence off-stage participants.
// Each remote's audio renders exactly once; local audio is never played back.
function AudioSink({ participants }: { participants: CallParticipant[] }) {
  return (
    <div aria-hidden style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
      {remoteAudioParticipants(participants).map((p) => <AudioEl key={p.sessionId} track={p.audioTrack} />)}
    </div>
  )
}

// ---- icons (our components; Daily renders nothing) --------------------------
const svg = (path: string) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><path d={path} /></svg>
)
const Icon = {
  mic: () => svg('M12 4a3 3 0 0 1 3 3v5a3 3 0 0 1-6 0V7a3 3 0 0 1 3-3zM6 11a6 6 0 0 0 12 0M12 17v3'),
  micOff: () => (<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><path d="M15 8V7a3 3 0 0 0-5.9-.7M9 10v2a3 3 0 0 0 4.5 2.6M6 11a6 6 0 0 0 9.3 5M18 11a6 6 0 0 1-.3 1.8M12 19v2M4 3l16 16" /></svg>),
  cam: () => svg('M3 8a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM16 10.5l5-3v9l-5-3'),
  camOff: () => (<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><path d="M16 16H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h1M9.5 6H14a2 2 0 0 1 2 2v4M21 7.5v9l-5-3M4 3l16 16" /></svg>),
  screen: () => svg('M3 5h18v11H3zM8 20h8M12 16v4'),
  leave: () => svg('M15 17l5-5-5-5M20 12H9M9 5H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h3'),
  layout: () => svg('M3 4h18v16H3zM3 10h18M9 10v10'),
  people: () => svg('M16 20a4 4 0 0 0-8 0M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8M20 20a3.5 3.5 0 0 0-4-3.3'),
  chat: () => svg('M5 5h14v10H9l-4 4z'),
  device: () => svg('M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 13a7.6 7.6 0 0 0 0-2l2-1.5-2-3.5-2.4 1a7.6 7.6 0 0 0-1.7-1L15 3H9l-.3 2a7.6 7.6 0 0 0-1.7 1l-2.4-1-2 3.5L2.6 11a7.6 7.6 0 0 0 0 2l-2 1.5 2 3.5 2.4-1a7.6 7.6 0 0 0 1.7 1L9 21h6l.3-2a7.6 7.6 0 0 0 1.7-1l2.4 1 2-3.5z'),
  pin: () => svg('M9 4h6l-1 6 3 3v2H7v-2l3-3zM12 15v5'),
  close: () => svg('M6 6l12 12M18 6L6 18'),
}

// ---- participant tile -------------------------------------------------------
function ParticipantTile({ p, big, reconnecting, pinned, canPin, onTogglePin }: {
  p: CallParticipant; big?: boolean; reconnecting?: boolean; pinned?: boolean; canPin?: boolean; onTogglePin?: () => void
}) {
  const speaking = tileState(p, reconnecting) === 'speaking'
  return (
    <div className={big ? undefined : 'cr-tile'} style={big ? { position: 'absolute', inset: 0 } : undefined} data-speaking={speaking || undefined} data-pinned={pinned || undefined}>
      {p.videoOn && p.videoTrack ? (
        <VideoEl track={p.videoTrack} muted mirror={p.isLocal} />
      ) : (
        <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
          <span style={{ width: big ? 92 : 52, height: big ? 92 : 52, borderRadius: '50%', display: 'grid', placeItems: 'center', fontFamily: MONO, fontWeight: 800, fontSize: big ? 30 : 17, color: '#cbd3df', background: 'linear-gradient(150deg, rgba(8,145,178,0.35), rgba(37,99,235,0.25))', border: '1px solid rgba(255,255,255,0.12)' }}>{initialsOf(p.name)}</span>
        </div>
      )}
      {reconnecting &&<div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', background: 'rgba(3,4,8,0.55)', fontFamily: MONO, fontSize: 11, color: WARN }}>reconnecting…</div>}
      <div style={{ position: 'absolute', left: 8, bottom: 8, right: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#eef2f7', background: 'rgba(0,0,0,0.5)', padding: '3px 8px', borderRadius: 6 }}>{p.name}{p.isLocal ? ' (you)' : ''}</span>
        {p.isOwner && <span title="Host — server-verified owner token" style={{ fontFamily: MONO, fontSize: 8.5, fontWeight: 800, color: '#ffce7a', background: 'rgba(0,0,0,0.5)', borderRadius: 5, padding: '2px 6px' }}>HOST</span>}
        {!p.audioOn && <span title="Muted" style={{ color: '#ff7a7a', display: 'inline-flex', background: 'rgba(0,0,0,0.5)', borderRadius: 6, padding: 3 }}>{Icon.micOff()}</span>}
        {p.screenOn && <span style={{ fontFamily: MONO, fontSize: 8.5, color: '#7fe6ff', background: 'rgba(0,0,0,0.5)', borderRadius: 5, padding: '2px 6px' }}>SHARING</span>}
      </div>
      {canPin && onTogglePin && (
        <button type="button" onClick={onTogglePin} aria-pressed={pinned} aria-label={pinned ? `Unpin ${p.name}` : `Pin ${p.name}`} title={pinned ? 'Unpin' : 'Pin to stage'} style={{ position: 'absolute', top: 8, right: 8, width: 30, height: 30, display: 'grid', placeItems: 'center', borderRadius: 8, border: '1px solid rgba(255,255,255,0.14)', background: pinned ? 'rgba(0,170,210,0.25)' : 'rgba(0,0,0,0.45)', color: pinned ? '#7fe6ff' : '#cbd3df', cursor: 'pointer' }}>{Icon.pin()}</button>
      )}
    </div>
  )
}

// ---- control dock button ----------------------------------------------------
function Ctl({ label, icon, off, active, danger, onClick, expanded, controls }: { label: string; icon: React.ReactNode; off?: boolean; active?: boolean; danger?: boolean; onClick?: () => void; expanded?: boolean; controls?: string }) {
  return (
    <button type="button" className="cr-ctl" data-off={off || undefined} data-active={active || undefined} data-danger={danger || undefined} onClick={onClick} aria-label={label} title={label} aria-expanded={expanded} aria-controls={controls}>
      <span className="cr-ctl__icon">{icon}</span>
      <span className="cr-ctl__label">{label}</span>
    </button>
  )
}

// ---- honest state card ------------------------------------------------------
function StateCard({ tone, title, body, children }: { tone: 'info' | 'ok' | 'warn' | 'danger'; title: string; body: string; children?: React.ReactNode }) {
  const c = tone === 'ok' ? OK : tone === 'warn' ? WARN : tone === 'danger' ? DANGER : ACCENT
  return (
    <div style={{ display: 'grid', placeItems: 'center', height: '100%', padding: 24 }}>
      <div style={{ maxWidth: 460, textAlign: 'center' }}>
        <div style={{ display: 'inline-block', fontFamily: MONO, fontSize: 9.5, fontWeight: 800, letterSpacing: '0.14em', color: c, border: `1px solid ${c}44`, background: c + '18', borderRadius: 6, padding: '4px 10px', marginBottom: 14 }}>{title}</div>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: MUTED, lineHeight: 1.65 }}>{body}</p>
        {children}
      </div>
    </div>
  )
}
const studioBtn = (accent: string, filled?: boolean): React.CSSProperties => ({ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '10px 16px', borderRadius: 10, cursor: 'pointer', fontSize: 12.5, fontWeight: 800, fontFamily: MONO, letterSpacing: '0.03em', background: filled ? accent : accent + '18', border: `1px solid ${accent}${filled ? '' : '44'}`, color: filled ? '#02121a' : accent })

export interface ConferenceCallRoomProps {
  title: string
  meetingMode?: string
  roomName?: string
  /** Display hint from the caller; protection is NOT asserted without evidence. */
  protectedRoom?: boolean
  /** The real sq_meetings id — needed by panels that scope to this meeting
   *  (Tasks, Approvals, the waiting-room admit rail). Not the Daily room name. */
  meetingId?: string
  /** sq_meetings.context_id when this call was started from a real
   *  Communications channel. Absent means Chat stays honestly blocked. */
  channelId?: string | null
  /** The caller's real account permission (role level >= 80) — gates
   *  Approvals decide actions. Independent of Daily's isOwner/host flag. */
  canDecideApprovals?: boolean
  onExit: () => void
}

export function ConferenceCallRoom({ title, meetingMode, roomName, protectedRoom, meetingId, channelId, canDecideApprovals, onExit }: ConferenceCallRoomProps) {
  const { snapshot, controls } = useCall()
  const { phase, participants, local, message, networkWarning } = snapshot

  const [layout, setLayout] = useState<RoomLayout | null>(null)
  const [pinnedId, setPinnedId] = useState<string | null>(null)
  const [panel, setPanel] = useState<PanelDef['key'] | null>(null)
  const [menu, setMenu] = useState<'layout' | 'device' | null>(null)
  const [devices, setDevices] = useState<MediaDevice[]>([])
  const [elapsed, setElapsed] = useState(0)
  const [waitingCount, setWaitingCount] = useState(0)
  const [waitingPanelOpen, setWaitingPanelOpen] = useState(false)

  const inCall = phase === 'joined' || phase === 'reconnecting'
  const reconnecting = phase === 'reconnecting'
  const screenSharer = participants.find((p) => p.screenOn && p.screenTrack) ?? null
  const activeSpeakerId = participants.find((p) => p.isActiveSpeaker && p.audioOn)?.sessionId ?? null
  const resolved = resolveLayout(layout, participants.length, Boolean(screenSharer))
  const layoutOptions = availableLayouts(participants.length, Boolean(screenSharer))
  const stage = stageParticipant(participants, pinnedId, activeSpeakerId)
  const filmstrip = participants.filter((p) => p.sessionId !== stage?.sessionId)
  const localParticipant = participants.find((p) => p.isLocal)
  const isHost = Boolean(localParticipant?.isOwner)

  useEffect(() => {
    if (!inCall) return
    const id = window.setInterval(() => setElapsed((v) => v + 1), 1000)
    return () => window.clearInterval(id)
  }, [inCall])

  // Host-only waiting-room count. waitingRoom.sessions() 404s for anyone
  // who isn't this meeting's creator or a role>=80 admin, so this is
  // gated purely on the Daily-verified isHost signal to avoid firing a
  // request every non-host participant would just see rejected.
  useEffect(() => {
    if (!inCall || !isHost || !meetingId) { setWaitingCount(0); return }
    let cancelled = false
    const load = async () => {
      try {
        const res = await api.commandCenter.meetings.waitingRoom.sessions(meetingId)
        if (!cancelled) setWaitingCount(res.sessions.filter((s) => s.status === 'waiting').length)
      } catch {
        // Honest no-op — the badge simply doesn't update this tick.
      }
    }
    void load()
    const id = window.setInterval(load, 8000)
    return () => { cancelled = true; window.clearInterval(id) }
  }, [inCall, isHost, meetingId])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (menu) { setMenu(null); return }
      if (panel) setPanel(null)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [menu, panel])

  const openDeviceMenu = () => {
    setMenu((m) => (m === 'device' ? null : 'device'))
    void controls.listDevices().then(setDevices)
  }
  const leaveAll = async () => { await controls.leave(); onExit() }
  const togglePanel = (key: PanelDef['key']) => setPanel((p) => (p === key ? null : key))
  const togglePin = (id: string) => setPinnedId((cur) => (cur === id ? null : id))

  const mm = String(Math.floor(elapsed / 60)).padStart(2, '0')
  const ss = String(elapsed % 60).padStart(2, '0')

  return (
    <div className="cr-shell" data-sqv-surface="conference-room" role="dialog" aria-modal="true" aria-label={`Conference room — ${title}`}>
      <div role="status" aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>
        {phaseAnnouncement(phase, networkWarning)}
      </div>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)', position: 'relative', zIndex: 1 }}>
        <button type="button" onClick={leaveAll} title="Leave and return to Conference Calls" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: '1px solid rgba(255,255,255,0.12)', color: MUTED, borderRadius: 9, padding: '6px 11px', cursor: 'pointer', fontFamily: MONO, fontSize: 11, fontWeight: 700 }}>{Icon.leave()} EXIT</button>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#f0f3f8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
          <div style={{ fontSize: 10, fontFamily: MONO, color: '#5c6675' }}>{(meetingMode || 'conference call').replace(/_/g, ' ')}{roomName ? ` · room ${roomName}` : ''}{inCall ? ` · ${mm}:${ss}` : ''} · secure headless media</div>
        </div>
        {inCall && <span style={{ fontFamily: MONO, fontSize: 10, color: MUTED }}>{participants.length} in room</span>}
        {inCall && isHost && (
          <button
            type="button"
            onClick={() => setWaitingPanelOpen((v) => !v)}
            aria-pressed={waitingPanelOpen}
            aria-label={`Waiting room, ${waitingCount} waiting`}
            style={{ fontFamily: MONO, fontSize: 9, fontWeight: 800, letterSpacing: '0.08em', color: waitingCount > 0 ? WARN : MUTED, border: `1px solid ${(waitingCount > 0 ? WARN : MUTED)}44`, background: waitingPanelOpen ? 'rgba(255,255,255,0.06)' : 'none', borderRadius: 5, padding: '3px 8px', cursor: 'pointer' }}
          >
            {waitingCount} WAITING
          </button>
        )}
        {protectedRoom && (
          <span title="Olivium protection is verified server-side; the room cannot assert it locally." style={{ fontFamily: MONO, fontSize: 9, fontWeight: 800, letterSpacing: '0.1em', color: OLIVIUM, border: `1px solid ${OLIVIUM}66`, background: `${OLIVIUM}1f`, borderRadius: 5, padding: '3px 8px' }}>OLIVIUM · VERIFYING</span>
        )}
        <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 800, letterSpacing: '0.08em', color: inCall ? OK : ACCENT, border: `1px solid ${(inCall ? OK : ACCENT)}44`, background: (inCall ? OK : ACCENT) + '18', borderRadius: 5, padding: '3px 8px' }}>
          {phase === 'joined' ? 'LIVE' : phase === 'reconnecting' ? 'RECONNECTING' : phase.replace(/_/g, ' ').toUpperCase()}
        </span>
      </header>

      <div className="cr-body" data-panel={panel ? 'open' : undefined}>
        <div className="cr-main">
          {networkWarning && inCall && (
            <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 3, fontFamily: MONO, fontSize: 10.5, color: WARN, background: 'rgba(20,16,6,0.9)', border: `1px solid ${WARN}55`, borderRadius: 8, padding: '6px 12px' }}>Network degraded — quality reduced. Staying connected.</div>
          )}

          {phase === 'idle' && (
            <StateCard tone="info" title="PREPARE SECURE ROOM" body={`${title}${roomName ? ` · room ${roomName}` : ''}. Camera and microphone start only after you choose an option below — nothing is captured or transmitted before that action.`}>
              <div style={{ display: 'inline-flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
                <button type="button" onClick={controls.preview} style={studioBtn(ACCENT, true)}>Enable camera &amp; microphone</button>
                <button type="button" onClick={() => { controls.preview(); controls.setMic(false) }} style={studioBtn(ACCENT)}>Join muted</button>
              </div>
            </StateCard>
          )}
          {phase === 'loading_sdk' && <StateCard tone="info" title="STARTING" body="Loading the call engine on demand. No provider secret is present in the browser." />}
          {phase === 'requesting_permissions' && <StateCard tone="info" title="CAMERA · MIC" body="Requesting camera and microphone permission for your local preview. Nothing is transmitted until you Join." />}
          {phase === 'preview_ready' && (
            <div style={{ display: 'grid', placeItems: 'center', height: '100%', padding: 24, overflow: 'auto' }}>
              <div style={{ width: 'min(560px, 100%)' }}>
                <div className="cr-primary" style={{ aspectRatio: '16 / 10' }}>
                  {stage && stage.videoOn && stage.videoTrack ? <VideoEl track={stage.videoTrack} muted mirror /> : <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: MUTED, fontSize: 12, fontFamily: MONO }}>Camera off — preview hidden</div>}
                  <div style={{ position: 'absolute', left: 10, bottom: 10, fontSize: 11, fontWeight: 700, color: '#eef2f7', background: 'rgba(0,0,0,0.5)', padding: '3px 8px', borderRadius: 6 }}>You (preview)</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 22, margin: '16px 0' }}>
                  <Ctl label={local.micOn ? 'Mic on' : 'Mic off'} off={!local.micOn} icon={local.micOn ? Icon.mic() : Icon.micOff()} onClick={() => controls.setMic(!local.micOn)} />
                  <Ctl label={local.camOn ? 'Cam on' : 'Cam off'} off={!local.camOn} icon={local.camOn ? Icon.cam() : Icon.camOff()} onClick={() => controls.setCam(!local.camOn)} />
                  <Ctl label="Devices" icon={Icon.device()} onClick={openDeviceMenu} expanded={menu === 'device'} controls="cr-device-menu" />
                </div>
                {menu === 'device' && <DeviceMenu devices={devices} controls={controls} onClose={() => setMenu(null)} anchored={false} />}
                <div style={{ textAlign: 'center' }}>
                  <button type="button" onClick={controls.join} style={studioBtn(OK, true)}>Join call →</button>
                  <p style={{ margin: '12px auto 0', maxWidth: 440, fontSize: 10.5, fontFamily: MONO, color: '#5c6675', lineHeight: 1.6 }}>Joining uses the scoped, short-lived token minted server-side. Provider credentials remain server-side. This room renders your own tiles and controls — no Daily UI.</p>
                </div>
              </div>
            </div>
          )}
          {phase === 'joining' && <StateCard tone="info" title="CONNECTING" body="Joining the controlled room with your scoped token…" />}

          {inCall && (
            <div className="cr-stage" data-layout={resolved} role="group" aria-label="Video stage">
              {resolved === 'grid' && participants.map((p) => (
                <ParticipantTile key={p.sessionId} p={p} canPin={!p.isLocal} pinned={pinnedId === p.sessionId} onTogglePin={() => togglePin(p.sessionId)} reconnecting={reconnecting} />
              ))}
              {(resolved === 'spotlight' || resolved === 'filmstrip' || resolved === 'screen') && (
                <>
                  <div className="cr-primary">
                    {resolved === 'screen' && screenSharer ? (
                      <>
                        <VideoEl track={screenSharer.screenTrack} muted />
                        <div style={{ position: 'absolute', left: 10, bottom: 10, fontSize: 11, fontWeight: 700, color: '#eef2f7', background: 'rgba(0,0,0,0.55)', padding: '3px 8px', borderRadius: 6 }}>{screenSharer.name} · sharing screen</div>
                      </>
                    ) : stage ? (
                      <ParticipantTile p={stage} big reconnecting={reconnecting} pinned={pinnedId === stage.sessionId} />
                    ) : null}
                  </div>
                  {resolved !== 'spotlight' && filmstrip.length > 0 && (
                    <div className="cr-filmstrip" role="list" aria-label="Other participants">
                      {filmstrip.map((p) => (
                        <div key={p.sessionId} role="listitem">
                          <ParticipantTile p={p} canPin={!p.isLocal} pinned={pinnedId === p.sessionId} onTogglePin={() => togglePin(p.sessionId)} reconnecting={reconnecting} />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {inCall && <AudioSink participants={participants} />}
          {inCall && isHost && waitingPanelOpen && meetingId && (
            <WaitingRoomHostPanel meetingId={meetingId} onClose={() => setWaitingPanelOpen(false)} />
          )}

          {phase === 'left' && <StateCard tone="info" title="LEFT" body="You left the call. Your media was released and the call object destroyed — nothing keeps running."><button type="button" onClick={onExit} style={studioBtn(ACCENT)}>Back to Conference Calls</button></StateCard>}
          {phase === 'ended' && <StateCard tone="info" title="ENDED" body={message || 'This room has ended.'}><button type="button" onClick={onExit} style={studioBtn(ACCENT)}>Back to Conference Calls</button></StateCard>}
          {phase === 'denied' && <StateCard tone="danger" title="ACCESS DENIED" body={message || 'The room token denied access. This is enforced server-side; nothing is bypassed here.'}><button type="button" onClick={onExit} style={studioBtn(ACCENT)}>Back to Conference Calls</button></StateCard>}
          {phase === 'expired' && <StateCard tone="warn" title="TOKEN EXPIRED" body={message || 'The scoped join token expired. Return to Conference Calls and Join again to mint a fresh short-lived token.'}><button type="button" onClick={onExit} style={studioBtn(ACCENT)}>Back to Conference Calls</button></StateCard>}
          {phase === 'revoked' && <StateCard tone="danger" title="ACCESS REVOKED" body={message || 'This meeting session was revoked. Return to Conference Calls for a new authorization.'}><button type="button" onClick={onExit} style={studioBtn(ACCENT)}>Back to Conference Calls</button></StateCard>}
          {phase === 'locked' && <StateCard tone="warn" title="ROOM LOCKED" body={message || 'This meeting is currently locked by the host.'}><button type="button" onClick={onExit} style={studioBtn(ACCENT)}>Back to Conference Calls</button></StateCard>}
          {phase === 'device_error' && <StateCard tone="danger" title="DEVICE / PROVIDER" body={message || 'Camera or microphone unavailable. No media is captured until access is granted.'}><div style={{ display: 'inline-flex', gap: 10 }}><button type="button" onClick={controls.preview} style={studioBtn(ACCENT)}>Retry preview</button><button type="button" onClick={onExit} style={studioBtn(ACCENT)}>Back</button></div></StateCard>}

          {inCall && (
            <div className="cr-dock">
              <Ctl label={local.micOn ? 'Mute' : 'Unmute'} off={!local.micOn} icon={local.micOn ? Icon.mic() : Icon.micOff()} onClick={() => controls.setMic(!local.micOn)} />
              <Ctl label={local.camOn ? 'Stop cam' : 'Start cam'} off={!local.camOn} icon={local.camOn ? Icon.cam() : Icon.camOff()} onClick={() => controls.setCam(!local.camOn)} />
              <Ctl label={local.screenOn ? 'Stop share' : 'Share'} active={local.screenOn} icon={Icon.screen()} onClick={() => controls.setScreen(!local.screenOn)} />
              <Ctl label="View" icon={Icon.layout()} active={menu === 'layout'} onClick={() => setMenu((m) => (m === 'layout' ? null : 'layout'))} expanded={menu === 'layout'} controls="cr-layout-menu" />
              <Ctl label="Devices" icon={Icon.device()} active={menu === 'device'} onClick={openDeviceMenu} expanded={menu === 'device'} controls="cr-device-menu" />
              <Ctl label="People" icon={Icon.people()} active={panel === 'people'} onClick={() => togglePanel('people')} expanded={panel === 'people'} controls="cr-panel" />
              <Ctl label="Chat" icon={Icon.chat()} active={panel === 'chat'} onClick={() => togglePanel('chat')} expanded={panel === 'chat'} controls="cr-panel" />
              <Ctl label="Leave" danger icon={Icon.leave()} onClick={leaveAll} />

              {menu === 'layout' && (
                <div className="cr-menu" id="cr-layout-menu" role="menu" aria-label="Stage layout" style={{ left: 16 }}>
                  <div className="cr-menu-heading">Layout</div>
                  {layoutOptions.map((opt) => (
                    <button key={opt.id} type="button" role="menuitemradio" aria-checked={resolved === opt.id} className="cr-menu-item" onClick={() => { setLayout(opt.id); setMenu(null) }}>{opt.label}</button>
                  ))}
                </div>
              )}
              {menu === 'device' && <DeviceMenu devices={devices} controls={controls} onClose={() => setMenu(null)} anchored />}
            </div>
          )}
        </div>

        {panel && (
          <aside className="cr-panel" id="cr-panel" aria-label={`${panel} panel`}>
            <div className="cr-panel__tabs" role="tablist" aria-label="Room panels">
              {CONFERENCE_PANELS.map((p) => (
                <button key={p.key} type="button" role="tab" className="cr-tab" aria-selected={panel === p.key} onClick={() => setPanel(p.key)}>{p.label}</button>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 8px 0' }}>
              <button type="button" onClick={() => setPanel(null)} aria-label="Close panel" style={{ background: 'none', border: 'none', color: MUTED, cursor: 'pointer' }}>{Icon.close()}</button>
            </div>
            <div className="cr-panel__body" role="tabpanel">
              {panel === 'people' ? (
                <PeoplePanel participants={participants} pinnedId={pinnedId} onTogglePin={togglePin} />
              ) : panel === 'chat' && channelId ? (
                <ChatPanel channelId={channelId} />
              ) : panel === 'chat' ? (
                <HonestPanel def={{ ...CONFERENCE_PANELS.find((p) => p.key === 'chat')!, reason: "This call has no linked Communications channel — start it from a channel's \"Start a conference call\" button to enable Chat." }} />
              ) : panel === 'notes' && meetingId ? (
                <MeetingNotesPanel meetingId={meetingId} />
              ) : panel === 'files' && meetingId ? (
                <MeetingFilesPanel meetingId={meetingId} />
              ) : panel === 'tasks' ? (
                <TasksPanel meetingTitle={title} />
              ) : panel === 'approvals' && meetingId ? (
                <ApprovalsPanel meetingId={meetingId} canDecide={canDecideApprovals} />
              ) : (
                <HonestPanel def={CONFERENCE_PANELS.find((p) => p.key === panel)!} />
              )}
            </div>
          </aside>
        )}
      </div>
    </div>
  )
}

// ---- People panel (LIVE off the call object) --------------------------------
function PeoplePanel({ participants, pinnedId, onTogglePin }: { participants: CallParticipant[]; pinnedId: string | null; onTogglePin: (id: string) => void }) {
  if (participants.length === 0) return <p className="cr-state-note">No participants yet. People appear here as they join.</p>
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ fontFamily: MONO, fontSize: 10, color: MUTED, marginBottom: 2 }}>{participants.length} IN ROOM · live from the call</div>
      {participants.map((p) => (
        <div key={p.sessionId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 10, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <span style={{ width: 30, height: 30, borderRadius: '50%', display: 'grid', placeItems: 'center', fontFamily: MONO, fontWeight: 800, fontSize: 11, color: '#cbd3df', background: 'linear-gradient(150deg, rgba(8,145,178,0.35), rgba(37,99,235,0.25))', flex: '0 0 auto' }}>{initialsOf(p.name)}</span>
          <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}{p.isLocal ? ' (you)' : ''}</span>
          {p.isOwner && <span title="Host — server-verified owner token" style={{ fontFamily: MONO, fontSize: 8.5, fontWeight: 800, color: '#ffce7a' }}>HOST</span>}
          {p.isActiveSpeaker && p.audioOn && <span style={{ color: OK }}>{Icon.mic()}</span>}
          {!p.audioOn && <span style={{ color: '#ff7a7a' }}>{Icon.micOff()}</span>}
          {!p.isLocal && (
            <button type="button" onClick={() => onTogglePin(p.sessionId)} aria-pressed={pinnedId === p.sessionId} aria-label={pinnedId === p.sessionId ? `Unpin ${p.name}` : `Pin ${p.name}`} style={{ background: 'none', border: 'none', cursor: 'pointer', color: pinnedId === p.sessionId ? '#7fe6ff' : MUTED }}>{Icon.pin()}</button>
          )}
        </div>
      ))}
    </div>
  )
}

// ---- Honest gated panel -----------------------------------------------------
function HonestPanel({ def }: { def: PanelDef }) {
  const tone = def.status === 'deferred' ? MUTED : def.status === 'connected' ? OK : WARN
  const badge = def.status === 'deferred' ? 'DEFERRED' : def.status === 'blocked' ? 'BLOCKED' : def.status.toUpperCase()
  return (
    <div>
      <span className="cr-badge" style={{ color: tone }}>{badge}</span>
      <p className="cr-state-note">{def.reason}</p>
      <p className="cr-state-note" style={{ marginTop: 10, fontFamily: MONO, fontSize: 10.5, color: '#5c6675' }}>Owner: {def.owner}. This tab shows nothing fabricated — it activates only when its real handler is wired into the room.</p>
    </div>
  )
}

// ---- Device menu (real enumeration + switching) -----------------------------
function DeviceMenu({ devices, controls, onClose, anchored }: { devices: MediaDevice[]; controls: CallControls; onClose: () => void; anchored: boolean }) {
  const cams = devices.filter((d) => d.kind === 'videoinput')
  const mics = devices.filter((d) => d.kind === 'audioinput')
  const spks = devices.filter((d) => d.kind === 'audiooutput')
  const group = (label: string, list: MediaDevice[], onPick: (id: string) => void) => (
    <>
      <div className="cr-menu-heading">{label}</div>
      {list.length === 0 ? (
        <div className="cr-menu-item" aria-disabled style={{ color: MUTED, cursor: 'default' }}>None detected</div>
      ) : list.map((d) => (
        <button key={d.deviceId} type="button" role="menuitem" className="cr-menu-item" onClick={() => { onPick(d.deviceId); onClose() }}>{d.label}</button>
      ))}
    </>
  )
  return (
    <div className={anchored ? 'cr-menu' : ''} id="cr-device-menu" role="menu" aria-label="Devices" style={anchored ? { right: 16 } : { position: 'relative', margin: '0 auto 8px', maxWidth: 360, padding: 8, borderRadius: 12, background: 'rgba(9,11,20,0.98)', border: '1px solid rgba(120,160,255,0.28)' }}>
      {group('Camera', cams, controls.setCamera)}
      {group('Microphone', mics, controls.setMicrophone)}
      {group('Speaker', spks, controls.setSpeaker)}
      {devices.length === 0 && <p className="cr-state-note" style={{ padding: '4px 10px' }}>Grant camera/microphone first to list devices.</p>}
    </div>
  )
}

export default ConferenceCallRoom
